import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { updateTeamMember, getTeamMemberProjects, getRolePermissions, logActivity } from '../../../services/teamService';

const TeamMemberModal = ({ member, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [memberPermissions, setMemberPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    fullName: member?.fullName || '',
    email: member?.email || '',
    phone: member?.phone || '',
    department: member?.department || '',
    jobTitle: member?.jobTitle || '',
    role: member?.role || 'viewer',
    status: member?.status || 'active'
  });

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'operator', label: 'Operator' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }
  ];

  useEffect(() => {
    if (activeTab === 'projects') loadProjects();
    if (activeTab === 'permissions') loadPermissions();
  }, [activeTab]);

  const loadProjects = async () => {
    setProjectsLoading(true);
    try {
      const data = await getTeamMemberProjects(member?.id);
      setProjects(data);
    } catch (err) {
      console.error('Load projects error:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const data = await getRolePermissions(member?.role);
      setMemberPermissions(data);
    } catch (err) {
      console.error('Load permissions error:', err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (saveError) setSaveError('');
  };

  const handleSave = async () => {
    if (!formData?.fullName?.trim()) { setSaveError('Full name is required'); return; }
    setLoading(true);
    setSaveError('');
    try {
      const roleChanged = formData?.role !== member?.role;
      await updateTeamMember(member?.id, formData);
      await logActivity(
        roleChanged ? 'role_change' : 'update',
        'team_member',
        member?.id,
        { updated_fields: Object.keys(formData), new_role: formData?.role }
      );
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      setSaveError(error?.message || 'Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: member?.fullName || '',
      email: member?.email || '',
      phone: member?.phone || '',
      department: member?.department || '',
      jobTitle: member?.jobTitle || '',
      role: member?.role || 'viewer',
      status: member?.status || 'active'
    });
    setSaveError('');
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-error/10 text-error',
      operator: 'bg-accent/10 text-accent',
      viewer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    };
    return colors?.[role] || colors?.viewer;
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      inactive: 'bg-muted text-muted-foreground',
      pending: 'bg-warning/10 text-warning'
    };
    return colors?.[status] || colors?.pending;
  };

  const getPhaseColor = (phase) => {
    const colors = {
      planning: 'bg-blue-100 text-blue-800',
      permits: 'bg-yellow-100 text-yellow-800',
      foundation: 'bg-orange-100 text-orange-800',
      framing: 'bg-purple-100 text-purple-800',
      systems: 'bg-indigo-100 text-indigo-800',
      finishing: 'bg-pink-100 text-pink-800',
      completed: 'bg-success/10 text-success'
    };
    return colors?.[phase] || 'bg-muted text-muted-foreground';
  };

  const formatRole = (role) => role?.charAt(0)?.toUpperCase() + role?.slice(1);
  const formatStatus = (status) => status?.charAt(0)?.toUpperCase() + status?.slice(1);
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'User' },
    { id: 'projects', label: 'Projects', icon: 'FolderOpen' },
    { id: 'permissions', label: 'Permissions', icon: 'Shield' }
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                <Image
                  src={member?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member?.fullName || 'User')}&background=1B3A6B&color=fff&size=80`}
                  alt={`Profile picture of ${member?.fullName}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">{member?.fullName}</h2>
                <p className="text-sm text-muted-foreground mt-1">{member?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getRoleBadgeColor(member?.role)}`}>
                    {formatRole(member?.role)}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getStatusBadgeColor(member?.status)}`}>
                    {formatStatus(member?.status)}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Quick Actions */}
          {!isEditing ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" iconName="Edit" iconPosition="left" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
              {member?.email && (
                <Button
                  variant="outline" size="sm" iconName="Mail" iconPosition="left"
                  onClick={() => window.open(`mailto:${member?.email}`)}
                >
                  Email
                </Button>
              )}
              {member?.phone && (
                <Button
                  variant="outline" size="sm" iconName="Phone" iconPosition="left"
                  onClick={() => window.open(`tel:${member?.phone}`)}
                >
                  Call
                </Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleSave} loading={loading}>
                Save Changes
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-border overflow-x-auto">
          <div className="flex px-4 md:px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {saveError && (
                <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                  <p className="text-sm text-error">{saveError}</p>
                </div>
              )}

              {isEditing ? (
                <>
                  <Input label="Full Name" value={formData?.fullName} onChange={(e) => handleInputChange('fullName', e?.target?.value)} required />
                  <Input label="Email" type="email" value={formData?.email} onChange={(e) => handleInputChange('email', e?.target?.value)} required />
                  <Input label="Phone" value={formData?.phone} onChange={(e) => handleInputChange('phone', e?.target?.value)} />
                  <Input label="Department" value={formData?.department} onChange={(e) => handleInputChange('department', e?.target?.value)} />
                  <Input label="Job Title" value={formData?.jobTitle} onChange={(e) => handleInputChange('jobTitle', e?.target?.value)} />
                  <Select label="Role" options={roleOptions} value={formData?.role} onChange={(value) => handleInputChange('role', value)} />
                  <Select label="Status" options={statusOptions} value={formData?.status} onChange={(value) => handleInputChange('status', value)} />
                </>
              ) : (
                <>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Phone</label>
                        <p className="text-foreground mt-1">
                          {member?.phone ? (
                            <a href={`tel:${member?.phone}`} className="text-primary hover:underline">{member?.phone}</a>
                          ) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Email</label>
                        <p className="text-foreground mt-1">
                          <a href={`mailto:${member?.email}`} className="text-primary hover:underline">{member?.email}</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Work Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Department</label>
                        <p className="text-foreground mt-1">{member?.department || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Job Title</label>
                        <p className="text-foreground mt-1">{member?.jobTitle || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Role</label>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium mt-1 ${getRoleBadgeColor(member?.role)}`}>
                          {formatRole(member?.role)}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Status</label>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium mt-1 ${getStatusBadgeColor(member?.status)}`}>
                          {formatStatus(member?.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Last Login</label>
                        <p className="text-foreground mt-1">{formatDate(member?.lastLoginAt)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Member Since</label>
                        <p className="text-foreground mt-1">{formatDate(member?.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              {projectsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : projects?.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
                    <Icon name="FolderOpen" size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">No projects assigned</p>
                  <p className="text-xs text-muted-foreground">This member has not been assigned to any projects yet.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">{projects?.length} project{projects?.length !== 1 ? 's' : ''} assigned</p>
                  {projects?.map((assignment) => (
                    <div key={assignment?.id} className="p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {assignment?.projects?.customerName || 'Unnamed Project'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {assignment?.projects?.address || 'No address'}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPhaseColor(assignment?.projects?.phase)}`}>
                              {assignment?.projects?.phase?.charAt(0)?.toUpperCase() + assignment?.projects?.phase?.slice(1)}
                            </span>
                            {assignment?.role && (
                              <span className="text-xs text-muted-foreground">Role: {assignment?.role}</span>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-md font-medium flex-shrink-0 ${
                          assignment?.status === 'assigned' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {assignment?.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="space-y-4">
              {permissionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : (
                <>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Shield" size={18} className="text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Role: {formatRole(member?.role)}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Permissions are managed at the role level. Edit the member to change their role.
                    </p>
                  </div>

                  {memberPermissions?.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground">No permissions found for this role.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {memberPermissions?.map((rp) => (
                        <div
                          key={rp?.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground capitalize">
                              {rp?.permissions?.name?.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs text-muted-foreground">{rp?.permissions?.description}</p>
                          </div>
                          <div className="flex items-center gap-1 text-success">
                            <Icon name="Check" size={16} />
                            <span className="text-xs font-medium">Granted</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;